// src/components/pdf-table/PdfTable.js
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import EnhancedUpload from '../upload/Upload';
import ThreeDotLoader from '../ui/ThreeDotLoader';
import EditableDocumentName from './EditableDocumentName';
import DocumentSetSelector from '../document-sets/DocumentSetSelector';
import SelectionCheckbox from './SelectionCheckbox';
import BulkActionToolbar from './BulkActionToolbar';
import { useAuth } from '../../firebase/AuthContext';
import { incrementActionCount } from '../../hooks/useGuide';


import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  MeasuringStrategy,
  DragOverlay
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  defaultAnimateLayoutChanges
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Dot, Lock, TriangleAlert, ListFilter, ChevronsDownUp, ChevronsUpDown, CircleHelp, Plus } from 'lucide-react';
import EvidenceDataService from '../../services/EvidenceDataService';
import ScrollContainer from '../ui/ScrollContainer';
import SearchEmptyState from '../ui/SearchEmptyState';
import DocumentSetPill from '../document-sets/DocumentSetPill';
import IconButton from '../ui/IconButton';
import { useGuideForce } from '../../contexts/GuideForceContext';

// Define column spacing configuration
const COLUMN_SPACING = {
  dragHandle: { left: 'pl-app-sm', right: 'pr-app-sm' },
  exhibit: { left: '', right: '' },
  document: { left: 'pl-app-md', right: 'pr-app-md' },
  documentSets: { left: 'pl-app-md', right: 'pr-app-md' },
  pages: { left: '', right: 'pr-10' }
};

// Helper function to get spacing classes for a column
const getColumnSpacing = (columnId) => {
  const spacing = COLUMN_SPACING[columnId] || { left: 'pl-app-md', right: 'pr-app-md' };
  return `${spacing.left} ${spacing.right}`.trim();
};

// FIXED: Enhanced animation function to fully freeze index rows
function customAnimateLayoutChanges(args) {
  const { isSorting, items, previousItems, id } = args;
  
  // FIXED: If this is an index row, never animate
  if (id === 'INDEX_DOCUMENT') {
    return false;
  }
  
  // During sorting: allow default animations for draggable items
  if (isSorting) {
    return defaultAnimateLayoutChanges(args);
  }
  
  // If arrays changed, disable animation (catches reorder events)
  if (items !== previousItems) {
    return false; // Prevents double animation
  }
  
  return true; // Allow other animations (add/remove)
}

// Measuring config for better performance
const measuringConfig = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

// Efficient array comparison for string arrays (avoids JSON.stringify)
const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Sortable Table Row Component - FIXED with better index row handling
 */
const SortableTableRow = React.memo(({
  row,
  selectedPdfId,
  hoveredRowId,
  setHoveredRowId,
  onSelectPdf,
  onRemovePdf,
  activeId,
  isFiltered = false,
  lastSelectedRowIndex,
  setLastSelectedRowIndex,
  table,
  activePopoverId,
}) => {
  const file = row.original;
  const isSelected = file.id === selectedPdfId;
  const isDragging = activeId === file.id;
  const isHovered = hoveredRowId === file.id;
  const isError = file.status === 'ocr_failed' || file.status === 'error' || file.status === 'client_error';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ 
    id: file.id,
    disabled: file.isIndex || isFiltered,
    animateLayoutChanges: customAnimateLayoutChanges,
  });

  const isCheckboxSelected = row.getIsSelected(); // Add this line

  // For index rows, completely disable transforms to prevent sliding animations
  const style = file.isIndex ? {} : {
    transform: CSS.Transform.toString(transform),
    transition: isDragging 
      ? 'none'
      : transform 
        ? 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)'
        : transition,
  };

  const handleRowClick = (e) => {
    // Don't interfere with buttons or dragging
    if (e.target.closest('button') || isDragging) {
      return;
    }

    // Clicking on checkbox column area - let the checkbox handler deal with it
    const clickedCell = e.target.closest('td');
    const isCheckboxColumn = clickedCell?.cellIndex === 1;
    if (isCheckboxColumn) {
      return;
    }

    const file = row.original;

    // Index rows can be selected for viewing but not for multi-selection
    if (file.isIndex) {
      // Just select for viewing, no checkbox selection logic
      onSelectPdf(file.id);
      return;
    }

    // Get all selectable rows (excluding index)
    const allRows = table.getRowModel().rows;
    const selectableRows = allRows.filter(r => r.original.id !== 'INDEX_DOCUMENT');
    const currentRowIndex = selectableRows.findIndex(r => r.id === row.id);

    // Handle shift + click for range selection
    if (e.shiftKey) {
      e.preventDefault();

      // If we have an anchor point, select range
      if (lastSelectedRowIndex !== null && lastSelectedRowIndex !== currentRowIndex) {
        const start = Math.min(lastSelectedRowIndex, currentRowIndex);
        const end = Math.max(lastSelectedRowIndex, currentRowIndex);

        // Select all rows in the range
        selectableRows.forEach((r, idx) => {
          if (idx >= start && idx <= end) {
            r.toggleSelected(true);  // Force select
          }
        });
      } else {
        // No anchor or same row - just toggle this row
        row.toggleSelected();
      }

      // Update anchor to current row
      setLastSelectedRowIndex(currentRowIndex);
      return;
    }

    // Handle ctrl/cmd + click for multi-select
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      row.toggleSelected();
      // Update anchor to current row for future shift+clicks
      setLastSelectedRowIndex(currentRowIndex);
      return;
    }

    // Normal click - select for viewing only (no checkbox selection)
    // Update anchor to current row for future shift+clicks
    setLastSelectedRowIndex(currentRowIndex);
    // Select PDF for viewing
    onSelectPdf(file.id);
  };

  return (

    <tr
      ref={setNodeRef}
      data-row-id={file.id}
      style={{ ...style, height: '42px' }}
      className={`
        select-none relative cursor-pointer transition-colors duration-app-normal
        ${isError
          ? (isSelected ? 'bg-app-warning-300' : 'bg-app-warning-200 hover:bg-app-warning-300')
          : isCheckboxSelected
            ? 'bg-app-gray-200'
            : (isSelected ? 'bg-app-gray-200' : 'bg-white hover:bg-app-gray-200')
        }
        ${isDragging ? 'opacity-50 z-50' : ''}
        ${file.isIndex ? 'relative z-5' : ''}
      `}
      onClick={handleRowClick}
      onMouseEnter={() => setHoveredRowId(file.id)}
      onMouseLeave={() => {
        // Don't clear hover if this row has an open popover
        if (!activePopoverId || activePopoverId !== file.id) {
          setHoveredRowId(null);
        }
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className={`relative ${getColumnSpacing(cell.column.id)}`}
          style={{
            height: '42px',
            overflow: 'hidden'
          }}
        >
          <div className={`${isDragging ? 'pointer-events-none' : ''} min-w-0`}> {/* ADDED: min-w-0 to allow shrinking */}
            {/* FIXED: Enhanced drag handle column logic */}
            {cell.column.id === 'dragHandle' ? (
              <div className="flex items-center justify-center h-full">
                {isError ? (
                  // PRIORITY: Show TriangleAlert for error docs (persists when selected)
                  <TriangleAlert className='text-app-warning-800' size={16} />
                ) : file.showSpinner && !isHovered ? (
                  <ThreeDotLoader size={2.5} />
                ) : isSelected && isHovered && !isDragging ? (
                  file.isIndex ? (
                    <Lock className="text-app-gray-600" size={16} />
                  ) : (
                    <div
                      ref={setActivatorNodeRef}
                      {...attributes}
                      {...listeners}
                      className="cursor-grab active:cursor-grabbing"
                    >
                      {!isFiltered ? (
                        <div
                          ref={setActivatorNodeRef}
                          {...attributes}
                          {...listeners}
                          className="cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical className="text-app-gray-600 cursor-move" size={16} />
                        </div>
                      ) : null}
                    </div>
                  )
                ) : isSelected && !isHovered ? (
                  <Dot className="text-app-accent" size={16} />
                ) : !isSelected && isHovered && !isDragging ? (
                  file.isIndex ? (
                    <Lock className="text-app-gray-600" size={16} />
                  ) : (
                    <div
                      ref={setActivatorNodeRef}
                      {...attributes}
                      {...listeners}
                      className="cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical className="text-app-gray-600 cursor-move" size={16} />
                    </div>
                  )
                ) : file.showSpinner ? (
                  <ThreeDotLoader size={2.5} />
                ) : null}
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </div>
        </td>
      ))}
    </tr>
  );
}, (prevProps, nextProps) => {
  const prevFile = prevProps.row.original;
  const nextFile = nextProps.row.original;
  
  // Compare if THIS ROW's hover state changed (not just hoveredRowId globally)
  const prevIsHovered = prevProps.hoveredRowId === prevFile.id;
  const nextIsHovered = nextProps.hoveredRowId === nextFile.id;
  
  // Compare if THIS ROW's selected state changed
  const prevIsSelected = prevProps.selectedPdfId === prevFile.id;
  const nextIsSelected = nextProps.selectedPdfId === nextFile.id;
  
  // Compare if THIS ROW is being dragged
  const prevIsDragging = prevProps.activeId === prevFile.id;
  const nextIsDragging = nextProps.activeId === nextFile.id;
  
  // Compare if THIS ROW has an open popover
  const prevHasPopover = prevProps.activePopoverId === prevFile.id;
  const nextHasPopover = nextProps.activePopoverId === nextFile.id;
  
  // File-level data
  const fileEqual = (
    prevFile.id === nextFile.id &&
    prevFile.displayName === nextFile.displayName &&
    prevFile.name === nextFile.name &&
    prevFile.status === nextFile.status &&
    prevFile.showSpinner === nextFile.showSpinner &&
    prevFile.isIndex === nextFile.isIndex &&
    arraysEqual(prevFile.documentSets, nextFile.documentSets)
  );
  
  // Row-specific state (using derived booleans, not raw prop values)
  const stateEqual = (
    prevIsHovered === nextIsHovered &&
    prevIsSelected === nextIsSelected &&
    prevIsDragging === nextIsDragging &&
    prevHasPopover === nextHasPopover &&
    prevProps.isFiltered === nextProps.isFiltered &&
    prevProps.row.getIsSelected() === nextProps.row.getIsSelected()
  );
  
  return fileEqual && stateEqual;
});

SortableTableRow.displayName = 'SortableTableRow';


/**
 * TanStack PDF Table Component with FIXED index row handling
 */
const PdfTable = ({
  pdfFiles,
  allPdfFiles = pdfFiles, // Full unfiltered list for selection validation
  selectedPdfId,
  onSelectPdf,
  onRemovePdf,
  onReorderPdfs,
  isFiltered = false,
  onUpdateFileName,
  stampConfig = {},
  caseData = null,
  onFilesAdded,
  isUploading = false,
  uploadProgress = 0,
  hasSearchTerm = false,
  allDocumentsCount = 0,
  isLoading = false,
  documentSets = [],
  onCreateDocumentSet,
  onUpdateDocumentSets,
  onClearAllSetsFromDocument,
  onReorderSets,
  onBatchAddSetsToDocuments,
  onBatchRemoveSetsFromDocuments,
  selectedSetId = null, // NEW: current selected doc set from sidebar
  isSetsCollapsed = false, // Per-case UI preference
  onSetsCollapsedChange, // Callback to update collapse state
  onSelectionChange,
  onStartGuide,
  onStartTagGuide,
  filterErrors = false,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [isTableHovered, setIsTableHovered] = useState(false);
  const [lastSelectedRowIndex, setLastSelectedRowIndex] = useState(null);
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [draggedRowHeight, setDraggedRowHeight] = useState(null);

  const scrollContainerRef = useRef(null);
  const hoveredRowIdRef = useRef(null);
  const rowSelectionRef = useRef({});
  const isTableHoveredRef = useRef(false);

  const { currentUser } = useAuth();
  const { tagGuideStep } = useGuideForce();
  const tagGuideStepRef = useRef(null);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: isFiltered ? 999999 : 8,  // Effectively disable when filtered
      },
    }),
    useSensor(KeyboardSensor, {
      enabled: !isFiltered  // Disable keyboard sorting when filtered
    })
  );

  const documentsWithDisplayData = useMemo(() =>
    filterErrors
      ? pdfFiles.filter(f =>
          f.status === 'ocr_failed' || f.status === 'error' || f.status === 'client_error'
        )
      : pdfFiles,
  [filterErrors, pdfFiles]);

  hoveredRowIdRef.current = hoveredRowId;
  rowSelectionRef.current = rowSelection;
  tagGuideStepRef.current = tagGuideStep;
  // Step 5 forces checkboxes visible
  isTableHoveredRef.current = isTableHovered || tagGuideStep === 5;

  // Define columns for TanStack Table with individual spacing
  const columns = useMemo(() => [
    {
      id: 'dragHandle',
      header: '',
      cell: () => null,
    },
    {
      id: 'exhibit',
      header: ({ table }) => {
        const hasSelection = Object.keys(rowSelection).length > 0;

        // Show checkbox when table hovered OR any rows selected
        if (isTableHoveredRef.current || hasSelection) {
          const allSelectableRows = table.getRowModel().rows.filter(
            row => row.original.id !== 'INDEX_DOCUMENT'
          );
          const selectedCount = allSelectableRows.filter(
            row => row.getIsSelected()
          ).length;
          const isAllSelected = selectedCount === allSelectableRows.length && allSelectableRows.length > 0;
          const isSomeSelected = selectedCount > 0 && !isAllSelected;

          return (
            <SelectionCheckbox
              data-guide-target="tag-guide-select-all"
              checked={isAllSelected}
              indeterminate={isSomeSelected}
              onChange={() => {
                if (isAllSelected) {
                  allSelectableRows.forEach(row => row.toggleSelected(false));
                } else {
                  allSelectableRows.forEach(row => row.toggleSelected(true));
                }
              }}
            />
          );
        }

        // Show filter icon when table is filtered, otherwise show "Ex."
        if (isFiltered) {
          return <ListFilter className="text-app-accent -ml-[2px]" size={16} />;
        }

        return 'Ex.';
      },
      cell: ({ row, table }) => {
        const file = row.original;
        const isRowHovered = hoveredRowIdRef.current === file.id;
        const hasAnySelection = Object.keys(rowSelectionRef.current).length > 0;
        const isProcessing = file.showSpinner;
        const isError = file.status === 'ocr_failed' || file.status === 'error' || file.status === 'client_error';
        const isSelected = file.id === selectedPdfId;
        // Index rows always show exhibit number, never checkbox
        if (file.isIndex) {
          return (
            <div className="text-app-sm text-app-gray-900">
              {file.exhibitId}
            </div>
          );
        }

        // Show checkbox when: row hovered OR any rows selected OR table force-hovered (guide step 5)
        if (isRowHovered || hasAnySelection || isTableHoveredRef.current) {
          return (
            <SelectionCheckbox
              checked={row.getIsSelected()}
              onChange={(e) => {
                const wasSelected = row.getIsSelected();
                row.getToggleSelectedHandler()(e);
                if (!wasSelected) {
                  const allRows = table.getRowModel().rows;
                  const selectableRows = allRows.filter(r => r.original.id !== 'INDEX_DOCUMENT');
                  const rowIndex = selectableRows.findIndex(r => r.id === row.id);
                  setLastSelectedRowIndex(rowIndex);
                }
              }}
            />
          );
        }

        // Default: show exhibit number
        return (
          <div className={`text-app-sm h-full flex items-center ${
            isError ? 'text-app-warning-800' : (isProcessing ? 'text-app-gray-500' : 'text-app-gray-900')
          } ${
            isSelected ? 'font-semibold' : ''
          }`}>
            {file.exhibitId}
          </div>
        );
      },
    },
    {
      id: 'document',
      header: () => <span className="pl-[2px]">Document</span>,
      cell: ({ row }) => {
        const file = row.original;
        const isCurrentPdf = file.id === selectedPdfId;
        const isProcessing = file.showSpinner;
        const isError = file.status === 'ocr_failed' || file.status === 'error' || file.status === 'client_error';

        // All rows (including index) just show the document name
        return (
          <EditableDocumentName
            value={file.name || file.displayName || file.originalName?.replace(/\.[^.]+$/i, '') || 'Loading...'}
            onSave={(newName) => {
              if (onUpdateFileName) {
                onUpdateFileName(file.id, newName);
              }
            }}
            isSelected={isCurrentPdf}
            isProcessing={isProcessing}
            isIndex={file.isIndex}
            isError={isError}
          />
        );
      },
    },

    {
      id: 'documentSets',
      header: () => {
        // Show collapse button when table is hovered
        return (
          <div className="flex items-center gap-1 h-[16px] pl-[8px]">
            <span>Tags</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartTagGuide?.(e.currentTarget);
              }}
              className="text-app-gray-500 hover:text-app-gray-900 transition-colors"
              aria-label="Open tags guide"
            >
              <CircleHelp className="w-3 h-3" />
            </button>
            {isTableHoveredRef.current && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onSetsCollapsedChange?.(!isSetsCollapsed);
                }}
                size="sm"
                title={isSetsCollapsed ? "Expand tags" : "Collapse tags"}
              >
                {isSetsCollapsed ? <ChevronsUpDown /> : <ChevronsDownUp />}
              </IconButton>
            )}
          </div>
        );
      },
      cell: ({ row, table }) => {
        const file = row.original;

        // For Index row with selected set, show the pill
        const selectedSet = selectedSetId ? documentSets.find(s => s.id === selectedSetId) : null;

        if (file.isIndex) {
          if (selectedSet) {
            return (
              <div className="flex items-center min-w-0 pl-[8px]">
                <DocumentSetPill
                  documentSet={selectedSet}
                  onRemove={() => {}}
                  showRemove={false}
                  isDraggable={false}
                  isReadOnly={true}
                />
              </div>
            );
          }
          return null;
        }

        // Get selected sets with colors
        const selectedSets = (file.documentSets || [])
          .map(setId => documentSets.find(s => s.id === setId))
          .filter(Boolean);

        const isFirstRealDoc =
          table.getRowModel().rows.filter(r => !r.original.isIndex)[0]?.original.id === file.id;
        const forceRing = tagGuideStepRef.current === 3 && isFirstRealDoc;

        return (
          <DocumentSetSelector
            availableSets={documentSets}
            selectedSetIds={file.documentSets || []}
            onUpdateDocumentSets={(newSetIds) => onUpdateDocumentSets?.(file.id, newSetIds)}
            onCreateSet={onCreateDocumentSet}
            disabled={file.showSpinner}
            onPopoverOpenChange={(isOpen) => {
              setActivePopoverId(isOpen ? file.id : null);
            }}
            rowId={file.id}
            isCollapsed={isSetsCollapsed}
            forceRing={forceRing}
          />
        );
      },
    },
  ], [
    selectedPdfId,
    onUpdateFileName,
    documentSets,
    onUpdateDocumentSets,
    onCreateDocumentSet,
    onReorderSets,
    onClearAllSetsFromDocument,
    setActivePopoverId,
    isFiltered,
    selectedSetId,
    isSetsCollapsed,
    onStartTagGuide,
  ]);

  // Create table instance
  const table = useReactTable({
    data: documentsWithDisplayData,
    columns,
    state: {
      rowSelection
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => row.original.id !== 'INDEX_DOCUMENT',
    enableMultiRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id, // Use document ID instead of array index
  });

  // Get selected document IDs and count from rowSelection state directly
  // This includes ALL selected documents across all search contexts
  const selectedDocumentIds = useMemo(() => {
    // Get all valid document IDs from the full unfiltered list
    const allDocIds = new Set(allPdfFiles.map(doc => doc.id));

    // Filter rowSelection to only include documents that exist in the full dataset
    // This maintains selections across search filters
    return Object.keys(rowSelection).filter(docId =>
      rowSelection[docId] === true && allDocIds.has(docId)
    );
  }, [rowSelection, allPdfFiles]);

  const selectedCount = selectedDocumentIds.length;
  useEffect(() => {
    onSelectionChange?.(selectedCount);
  }, [selectedCount, onSelectionChange]);

  // Bulk action handlers
  const handleBulkDelete = useCallback((documentIds) => {
    documentIds.forEach(id => onRemovePdf?.(id));
    setRowSelection({});
    if (currentUser?.uid) {
      incrementActionCount(currentUser.uid, 'bulkDelete');
    }
  }, [onRemovePdf, currentUser?.uid]);

  const handleBulkAddSets = useCallback((documentIds, setIds) => {
    // Use batch operation instead of loop
    onBatchAddSetsToDocuments?.(documentIds, setIds);
  }, [onBatchAddSetsToDocuments]);

  const handleBulkRemoveSets = useCallback((documentIds, setIds) => {
    // Use batch operation instead of loop
    onBatchRemoveSetsFromDocuments?.(documentIds, setIds);
  }, [onBatchRemoveSetsFromDocuments]);

  // Clear anchor if the anchored row gets deselected
  useEffect(() => {
    if (lastSelectedRowIndex !== null) {
      const selectableRows = table.getRowModel().rows.filter(
        r => r.original.id !== 'INDEX_DOCUMENT'
      );
      const anchoredRow = selectableRows[lastSelectedRowIndex];

      // If the anchored row is no longer selected, clear the anchor
      if (anchoredRow && !anchoredRow.getIsSelected()) {
        setLastSelectedRowIndex(null);
      }
    }
  }, [rowSelection]);


  // Drag and drop handlers
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveId(active.id);
    setOverId(null);

    // Capture the height of the row being dragged
    const draggedElement = document.querySelector(`[data-row-id="${active.id}"]`);
    if (draggedElement) {
      setDraggedRowHeight(draggedElement.offsetHeight);
    }
  }, []);

  const handleDragOver = useCallback((event) => {
    const { over } = event;
    setOverId(over?.id || null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    setOverId(null);
    setActiveId(null);
    setDraggedRowHeight(null);

    if (active.id !== over?.id && over?.id) {
      const oldIndex = documentsWithDisplayData.findIndex(file => file.id === active.id);
      const newIndex = documentsWithDisplayData.findIndex(file => file.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newOrder = arrayMove(documentsWithDisplayData, oldIndex, newIndex);
        
        // FIXED: Pass correct format for CloudFileService.updateFileOrders
        // Filter out INDEX_DOCUMENT and map to {fileId, order} format
        const fileOrderUpdates = newOrder
          .filter(file => file.id !== 'INDEX_DOCUMENT')
          .map((file, index) => ({
            fileId: file.id,
            order: index
          }));
        
        onReorderPdfs?.(fileOrderUpdates);
        if (currentUser?.uid) {
          incrementActionCount(currentUser.uid, 'reorderDocs');
        }
      }
    }
  }, [documentsWithDisplayData, onReorderPdfs]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      measuring={measuringConfig}
    >
      <SortableContext items={documentsWithDisplayData.map(file => file.id)} strategy={verticalListSortingStrategy}>
        {/* Container query wrapper for toolbar responsive behavior */}
        <div className="@container/table flex flex-col max-h-full">
          {/* Table container - FIXED: Fits content but max out at current height */}
          <div
            className="relative bg-app-white border border-app-gray-900 rounded-app-lg overflow-hidden flex flex-col flex-1 min-h-0"
            onMouseEnter={() => setIsTableHovered(true)}
            onMouseLeave={() => {
              // Don't clear table hover state if a popover is open
              if (!activePopoverId) {
                setIsTableHovered(false);
              }
            }}
          >
          {/* Table wrapper for scrolling - FIXED: Flexible sizing with minimum height */}
          <ScrollContainer
            ref={scrollContainerRef}
            className="flex-1 min-h-0"
            trackMargins={{ top: 42, bottom: 4 }}
          >
            <table className="w-full" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              
              <colgroup>
                <col style={{ width: '32px', minWidth: '32px', maxWidth: '32px' }} />
                <col style={{ width: '28px', minWidth: '28px', maxWidth: '28px' }} />
                <col style={{ width: 'auto' }} />  {/* document - explicitly set to auto */}
                <col style={{ width: '160px', minWidth: '160px', maxWidth: '160px' }} />
              </colgroup>

              {/* Frozen header - always visible */}
              <thead className="sticky top-0 bg-app-white z-10">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`pt-app-xl pb-0 text-left text-app-gray-400 text-app-xs font-normal ${getColumnSpacing(header.column.id)}`}
                        style={{
                          width: header.column.id === 'dragHandle' ? '32px' :
                                header.column.id === 'exhibit' ? '28px' :
                                // header.column.id === 'pages' ? '64px' :
                                'auto',  // document column explicitly set to auto
                          // minWidth: header.column.id === 'pages' ? '64px' : undefined,
                          // maxWidth: header.column.id === 'pages' ? '64px' : undefined
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              {/* Table body - show rows if we have documents */}
              {isLoading ? (
                <tbody>
                  <tr>
                    <td colSpan={columns.length}>
                    </td>
                  </tr>
                </tbody>
              ) : (documentsWithDisplayData.length > 0 || tagGuideStep === 3) ? (
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <SortableTableRow
                      key={row.id}
                      row={row}
                      selectedPdfId={selectedPdfId}
                      hoveredRowId={hoveredRowId}
                      setHoveredRowId={setHoveredRowId}
                      onSelectPdf={onSelectPdf}
                      onRemovePdf={onRemovePdf}
                      activeId={activeId}
                      isFiltered={isFiltered}
                      lastSelectedRowIndex={lastSelectedRowIndex}
                      setLastSelectedRowIndex={setLastSelectedRowIndex}
                      table={table}
                      activePopoverId={activePopoverId}
                    />
                  ))}

                  {/* Visual-only skeleton row — TagGuide step 3, no real docs */}
                  {tagGuideStep === 3 && pdfFiles.filter(f => !f.isIndex).length === 0 && (
                    <tr className="pointer-events-none">
                      <td colSpan={4} className="px-app-sm py-app-xs">
                        <div className="h-[42px] bg-app-gray-200 rounded-app-md flex items-center">
                          <div className="w-8 flex-shrink-0" />
                          <div className="w-7 flex-shrink-0">
                            <div className="w-3 h-4 bg-app-gray-400 rounded-app-2xs" />
                          </div>
                          <div className="flex-1 px-app-md">
                            <div className="w-24 h-4 bg-app-gray-400 rounded-app-2xs" />
                          </div>
                          <div className="w-[160px] px-app-md">
                            <div
                              data-guide-target="tag-guide-first-tags-cell"
                              className="min-h-[32px] px-2 py-1 ring-1 ring-app-gray-400 rounded-app-sm flex items-center"
                            >
                              <Plus className="w-3.5 h-3.5 text-app-gray-500" />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              ) : null}
            </table>
          </ScrollContainer>

          {/* Search empty state - show when search returns no results */}
          {documentsWithDisplayData.length === 0 && hasSearchTerm && (
            <div className="mx-app-xl">
              <SearchEmptyState
                primaryText="No documents match your search."
                secondaryText="Try a different search or upload a new document."
              />
            </div>
          )}

          {/* Upload component - auto-opens only when truly no documents (not due to search) */}
          <EnhancedUpload
            onFilesAdded={onFilesAdded}
            disabled={isUploading}
            autoOpen={allDocumentsCount === 0 && !hasSearchTerm && !isLoading && !(tagGuideStep === 3 && pdfFiles.filter(f => !f.isIndex).length === 0)}
            hasSelections={selectedCount > 0}
          />

        </div>

          {/* Bulk Action Toolbar - always below table; flex layout shrinks table when needed */}
          {selectedCount > 0 && (
            <div className="mt-app-md">
              <BulkActionToolbar
                selectedCount={selectedCount}
                selectedDocumentIds={selectedDocumentIds}
                documentSets={documentSets}
                pdfFiles={allPdfFiles}
                onAddSetsToDocuments={handleBulkAddSets}
                onRemoveSetsFromDocuments={handleBulkRemoveSets}
                onDeleteDocuments={handleBulkDelete}
                onCreateDocumentSet={onCreateDocumentSet}
                onStartGuide={onStartGuide}
              />
            </div>
          )}
        </div>
      </SortableContext>

      {/* DragOverlay - maintains original row size during drag */}
      <DragOverlay dropAnimation={null}>
        {activeId ? (
          <div
            style={{
              height: draggedRowHeight ? `${draggedRowHeight}px` : '42px',
              opacity: 0.8,
            }}
          >
            <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
              <colgroup>
                <col style={{ width: '32px', minWidth: '32px', maxWidth: '32px' }} />
                <col style={{ width: '28px', minWidth: '28px', maxWidth: '28px' }} />
                <col style={{ width: 'auto' }} />
                <col style={{ width: '160px', minWidth: '160px', maxWidth: '160px' }} />
              </colgroup>
              <tbody>
                {(() => {
                  const draggedRow = table.getRowModel().rows.find(r => r.id === activeId);
                  if (!draggedRow) return null;

                  const file = draggedRow.original;
                  const isSelected = file.id === selectedPdfId;
                  const isCheckboxSelected = draggedRow.getIsSelected();
                  const isError = file.status === 'ocr_failed' || file.status === 'error' || file.status === 'client_error';

                  return (
                    <tr
                      className={`
                        select-none relative cursor-grabbing
                        ${isError
                          ? 'bg-app-warning-200'
                          : isCheckboxSelected
                            ? 'bg-app-gray-200'
                            : (isSelected ? 'bg-app-gray-200' : 'bg-white')
                        }
                      `}
                      style={{ height: draggedRowHeight ? `${draggedRowHeight}px` : '42px' }}
                    >
                      {draggedRow.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`relative ${getColumnSpacing(cell.column.id)}`}
                          style={{
                            height: draggedRowHeight ? `${draggedRowHeight}px` : '42px',
                            overflow: 'hidden'
                          }}
                        >
                          <div className="min-w-0">
                            {cell.column.id === 'dragHandle' ? (
                              <div className="flex items-center justify-center h-full">
                                <GripVertical className="text-app-gray-600" size={16} />
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default PdfTable;